// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Contexto para manejar modales globalmente
 * Reemplaza alert(), confirm(), y prompt()
 * DEV ERROR UNIFIED OVERLAY: showError puede recibir devErrorDetail para un solo modal con detalle técnico en dev.
 */

import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLanguage } from './LanguageContext';
import Modal from '../components/UI/Modal';
import { setApiErrorHandler } from '../services/api';
import { isDevErrorUnifiedModal } from '../config/devErrorOverlay';

const ModalContext = createContext(null);

/**
 * Fallbacks centralizados SOLO para keys críticas de modales.
 * Regla: permitidos únicamente aquí.
 * Idioma base: español.
 */
const CRITICAL_FALLBACKS = {
  'auth.email_send_failed': 'No pudimos enviar el email ahora. Reintentá o cambiá el email.',
  'auth.email_not_verified': 'Debés confirmar tu email para activar la cuenta.',
  'auth.register.email_failed.title': 'Cuenta creada',
  'auth.register.email_failed.message':
    'Tu cuenta fue creada correctamente, pero hubo un problema al enviar el email de confirmación. Revisá spam o solicitá un nuevo email.',
  'auth.register.success.title': 'Cuenta creada',
  'auth.register.success.message':
    'Cuenta creada exitosamente. Se ha enviado un email de confirmación. Revisa tu bandeja de entrada (y la carpeta de spam) y haz clic en el enlace para activar tu cuenta.',
  'auth.email_send_failed_title': 'No pudimos enviar el email',
  'auth.resend_sent': 'Email de confirmación reenviado.',

  // Perfil
  'profile.photo.upload.success': 'Foto de perfil actualizada correctamente',
  'profile.photo.upload.success.title': 'Foto Actualizada',
  'profile.photo.upload.error': 'Error al subir la foto de perfil. Por favor, intenta nuevamente.',
  'profile.photo.upload.error.title': 'Error al Subir Foto',
  'profile.avatar.error_title': 'No se pudo actualizar el avatar',
  'profile.avatar.file_too_large': 'La imagen supera el tamaño máximo permitido (2 MB).',
  'profile.avatar.file_too_large_hint': 'Elegí una imagen más liviana o reducí su tamaño e intentá nuevamente.',
  'profile.avatar.tooLarge.title': 'Imagen demasiado grande',
  'profile.avatar.tooLarge.message': 'La foto supera el tamaño permitido. El máximo es 2 MB.',
  'profile.avatar.uploadError.title': 'No se pudo actualizar el avatar',
  'profile.avatar.uploadError.message': 'Ocurrió un problema al subir la imagen. Intenta nuevamente.',
  'profile.save.success': 'Perfil actualizado correctamente',
  'profile.save.success.title': 'Perfil actualizado',
  'profile.saveSuccess.title': 'Perfil actualizado',
  'profile.saveSuccess.message': 'Tus cambios se guardaron correctamente.',
  'profile.update.success.title': 'Perfil Actualizado',
  'profile.update.success.message': 'Tus cambios se guardaron correctamente.',

  // Login / recovery
  'login.passwordRecovery.success': 'Se ha enviado un enlace de recuperación a tu email.',
  'login.passwordRecovery.successTitle': 'Email enviado',
  'login.passwordRecovery.successMessage':
    'Hemos enviado un email con las instrucciones para restablecer tu contraseña. Revisá tu bandeja de entrada y la carpeta de spam.',
  'login.passwordRecovery.devExplanation':
    'En modo desarrollo el email no se envía a tu bandeja; se guarda para previsualización.',
  'login.passwordRecovery.devSinkNotice':
    'Modo desarrollo: este email NO fue enviado. Usá "Ver email" para abrir el mensaje.',

  // Genéricos
  'errors.generic.title': 'Error',
  'errors.generic.message': 'Ocurrió un error. Intentá nuevamente.',

  'auth.password_incorrect': 'La contraseña es incorrecta.',
  'auth.login.password_error_title': 'Contraseña incorrecta',
  'auth.login.password_attempts_hint':
    'Tienes 3 oportunidades para ingresar tu contraseña correctamente. Si superas esos intentos, deberás restablecer tu contraseña.',
  'auth.login.password_attempts_exhausted_title': 'Límite de intentos alcanzado',
  'auth.login.password_attempts_exhausted_body':
    'Superaste los 3 intentos permitidos. Para continuar, restablecé tu contraseña desde "¿Olvidaste tu contraseña?".',
  'auth.login.go_to_reset_password': 'Ir a restablecer contraseña',

  'auth.login.password_remaining_two': 'Te quedan 2 oportunidades.',
  'auth.login.password_remaining_one': 'Te queda 1 oportunidad.',
  'auth.login.password_attempts_locked_inline':
    'Has alcanzado el máximo de intentos. Debes restablecer tu contraseña.'
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal debe usarse dentro de ModalProvider');
  }
  return context;
};

export const ModalProvider = ({ children }) => {
  const { t } = useLanguage();

  const [modal, setModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    content: null,
    hideFooter: false,
    onConfirm: null,
    onCancel: null,
    confirmText: '',
    cancelText: '',
    showCancel: false,
    devErrorDetail: null,
    modalType: null,
  });

  const closeModal = useCallback(() => {
    setModal(prev => ({ ...prev, isOpen: false, devErrorDetail: null }));
  }, []);

  useEffect(() => {
    if (modal.isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [modal.isOpen]);

  /**
   * Normaliza cualquier valor a string seguro.
   * Nunca devuelve undefined.
   */
  const normalizeMessage = useCallback(
    (value, fallbackKey = 'errors.generic.message') => {
      if (typeof value === 'string' && value.trim()) {
        return value;
      }

      if (value == null) {
        return t(fallbackKey);
      }

      if (value instanceof Error) {
        return value.message || t(fallbackKey);
      }

      if (typeof value === 'object') {
        const code = value.code;
        if (typeof code === 'string') {
          const translated = t(code);
          if (translated && translated !== code) {
            return translated;
          }
          if (CRITICAL_FALLBACKS[code]) {
            return CRITICAL_FALLBACKS[code];
          }
        }
      }

      try {
        return String(value);
      } catch {
        return t(fallbackKey);
      }
    },
    [t]
  );

  const looksLikeI18nKey = (str) =>
    typeof str === 'string' && str.includes('.') && !str.includes(' ');

  const resolveWithFallback = useCallback(
    (key) => {
      if (!looksLikeI18nKey(key)) return key;
      const translated = t(key);
      if (translated && translated !== key && translated.trim()) {
        return translated;
      }
      return CRITICAL_FALLBACKS[key] || translated || '—';
    },
    [t]
  );

  const showModal = useCallback(
    (options) => {
      const devErrorDetail = options.devErrorDetail ?? null;
      if (options.content) {
        const titleKey = options.title || 'info.title';
        const resolvedTitle = typeof titleKey === 'string' && !titleKey.includes('.') ? titleKey : resolveWithFallback(titleKey);
        setModal({
          isOpen: true,
          title: resolvedTitle,
          message: '',
          type: options.type || 'form',
          content: options.content,
          hideFooter: options.hideFooter === true,
          onConfirm: options.onConfirm || null,
          onCancel: options.onCancel || null,
          confirmText: options.confirmText || t('buttons.close'),
          cancelText: options.cancelText || t('buttons.cancel'),
          showCancel: options.showCancel === true,
          devErrorDetail,
          modalType: options.modalType || null,
        });
        return;
      }

      const rawTitle = normalizeMessage(options.title, 'errors.generic.title');
      const rawMessage = normalizeMessage(options.message, 'errors.generic.message');

      setModal({
        isOpen: true,
        title: resolveWithFallback(rawTitle),
        message: resolveWithFallback(rawMessage),
        type: options.type || 'info',
        content: null,
        hideFooter: false,
        onConfirm: options.onConfirm || null,
        onCancel: options.onCancel || null,
        confirmText: options.confirmText || t('buttons.accept'),
        cancelText: options.cancelText || t('buttons.cancel'),
        showCancel: options.showCancel === true,
        devErrorDetail,
        modalType: options.modalType || null,
      });
    },
    [normalizeMessage, resolveWithFallback, t]
  );

  // API pública
  const alert = useCallback(
    (message, title) =>
      new Promise(resolve => {
        showModal({
          title: title ?? 'info.title',
          message,
          type: 'info',
          onConfirm: resolve
        });
      }),
    [showModal]
  );

  /**
   * Bloqueo explícito: cualquier invocación directa a showInfo falla en desarrollo/revisión.
   * La API pública para modales informativos es únicamente safeShowInfo (usa alert internamente).
   */
  const showInfo = useCallback(() => {
    throw new Error('[ARCHAT_BLOCKED] showInfo está prohibido. Usar safeShowInfo');
  }, []);

  const safeShowInfo = useCallback(
    (...args) => {
      try {
        if (typeof showInfo === 'function') return alert(...args);
      } catch (e) {
        console.warn('[SAFE_MODAL_FALLBACK]', e?.message ?? e);
        return alert(...args);
      }
      console.error('[INVALID_FN] safeShowInfo', showInfo);
      return Promise.resolve();
    },
    [alert, showInfo]
  );

  const confirm = useCallback(
    (message, title) =>
      new Promise(resolve => {
        showModal({
          title: title ?? 'buttons.confirm',
          message,
          type: 'confirm',
          showCancel: true,
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false)
        });
      }),
    [showModal]
  );

  const showSuccess = useCallback(
    (message, title) =>
      new Promise(resolve => {
        showModal({
          title: title ?? 'modal.success.title',
          message,
          type: 'success',
          onConfirm: resolve
        });
      }),
    [showModal]
  );

  const showError = useCallback(
    (message, title, options = null) =>
      new Promise(resolve => {
        const devErrorDetail = options && typeof options === 'object' && options.devErrorDetail ? options.devErrorDetail : null;
        showModal({
          title: title ?? 'errors.generic.title',
          message,
          type: 'error',
          onConfirm: resolve,
          devErrorDetail: devErrorDetail || null,
        });
      }),
    [showModal]
  );

  useEffect(() => {
    if (!isDevErrorUnifiedModal()) return;
    setApiErrorHandler((payload) => {
      showError(payload.userMessage || t('errors.generic.title'), t('errors.generic.title'), { devErrorDetail: payload });
    });
    return () => setApiErrorHandler(null);
  }, [showError, t]);

  const showWarning = useCallback(
    (message, title) =>
      new Promise(resolve => {
        showModal({
          title: title ?? 'modal.warning.title',
          message,
          type: 'warning',
          onConfirm: resolve
        });
      }),
    [showModal]
  );

  const value = {
    showModal,
    closeModal,
    alert,
    safeShowInfo,
    confirm,
    showSuccess,
    showError,
    showWarning
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        content={modal.content}
        hideFooter={modal.hideFooter}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
        confirmText={modal.confirmText}
        cancelText={modal.cancelText}
        showCancel={modal.showCancel}
        devErrorDetail={modal.devErrorDetail}
        modalType={modal.modalType}
      />
    </ModalContext.Provider>
  );
};
